// The pages of a book as HTML - ONE place, used by the book page (book.js) and
// the reader (reader.js), so both look exactly like the printed book (books/pdf.py
// follows the same layout). Since 2026-10-15 every book has 32 inner pages (the
// print partner's hardcover: the cover is separate, page 1 is a right-hand page):
//
//   cover · [inside cover | p1 title page] · [p2 foreword | p3 "Unsere Helden"] ·
//   [picture p2k+2 | text p2k+3] × n · [p32 closing + "Ende" | inside cover] · back cover
//
// n = 14 -> 2n + 4 = 32 inner pages; older books have 8 or 12 spreads, same order.
//
// Sizes inside a page are in cqw (percent of the page width), so a page is a
// scaled print page; `fitText` shrinks a long text until it fits its page and
// remembers the factor, so a redraw (Idiomorph) never makes it jump.

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[c]));

const ORNAMENT = '<svg viewBox="0 0 120 24" fill="none" aria-hidden="true"><path d="M4 12h38M78 12h38" stroke="currentColor" stroke-width="2" stroke-linecap="round" opacity=".55"/><path d="M60 2.5l2.8 6.4 6.9.7-5.2 4.6 1.5 6.8L60 17.5 54 21l1.5-6.8-5.2-4.6 6.9-.7z" fill="currentColor"/><circle cx="46" cy="12" r="2" fill="currentColor" opacity=".7"/><circle cx="74" cy="12" r="2" fill="currentColor" opacity=".7"/></svg>';

// The number on a text page is the story page (1..n), the same in the PDF - a child counts the story,
// not the printed sheets (decided 2026-10-15; the printer needs no folios).
const folio = {
  text: (k) => k,
  printed: (n) => 2 * n + 4,          // inner pages: title, foreword, heroes, n spreads, closing
};

// a page's text as paragraphs (real line breaks of the story = new paragraph / a line of dialogue)
function paragraphs(text) {
  const t = String(text || '').trim();
  // blank lines = stanzas (a poem): the lines of a stanza stay together; otherwise every line is a paragraph
  if (/\n\s*\n/.test(t)) {
    return t.split(/\n\s*\n/).map(b => b.split('\n').map(l => esc(l.trim())).filter(Boolean).join('<br>')).filter(Boolean).map(b => `<p>${b}</p>`).join('');
  }
  return t.split(/\n+/).map(l => l.trim()).filter(Boolean).map(l => `<p>${esc(l)}</p>`).join('');
}

const fits = {};                         // key -> fit factor found by fitText
const fitKey = (text) => {
  let h = 0;
  const s = String(text || '');
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return `${s.length}:${h}`;
};
const fitFor = (text, font, size) => fits[`${font}|${size}|${fitKey(text)}`] || 1;

function textPage(text, number, {side = 'is-right', editable = false, id = '', busy = false, font = '', size = ''} = {}) {
  const fit = fitFor(text, font, size);
  const attrs = editable ? ` role="button" tabindex="0" data-action="edit-text" data-id="${esc(id)}" aria-label="Text von Seite ${number} bearbeiten"` : '';
  return `<div class="bk-page bk-text ${side}${editable ? ' is-editable' : ''}${busy ? ' is-busy' : ''}" data-fit-text="${esc(fitKey(text))}" style="--fit:${fit}"${attrs}>
    ${editable ? '<span class="bk-hint">✏️ Text ändern</span>' : ''}
    <span class="bk-ornament">${ORNAMENT}</span>
    <div class="bk-text-body">${paragraphs(text)}</div>
    <span class="bk-folio">– ${number} –</span></div>`;
}

const HEART = '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 21s-7.5-4.6-9.6-9.3C.9 8.2 3 4.5 6.6 4.5c2.1 0 3.6 1.2 4.4 2.6.8-1.4 2.3-2.6 4.4-2.6 3.6 0 5.7 3.7 4.2 7.2C19.5 16.4 12 21 12 21z"/></svg>';

// p1, alone on the right: the title, who the book is for, the dedication - and a reserved spot at the bottom
// for the audio book's QR code (drawn only when it was bought; the code itself comes later: data-qr is the hook)
function titlePage(book, {side = 'is-right', editable = false} = {}) {
  const attrs = editable ? ' role="button" tabindex="0" data-action="edit-cover" aria-label="Titel und Widmung ändern"' : '';
  return `<div class="bk-page bk-titlepage ${side}${editable ? ' bk-text is-editable' : ''}"${attrs}>
    ${editable ? '<span class="bk-hint">✏️ Titel ändern</span>' : ''}
    <div class="bk-title-block">
      <h2>${esc(book.title)}</h2>
      <span class="bk-ornament">${ORNAMENT}</span>
      ${book.hero ? `<p class="bk-for">für ${esc(book.hero)}</p>` : ''}
      ${book.dedication ? `<p class="bk-dedication">${esc(book.dedication)}</p>` : ''}
    </div>
    <div class="bk-qr-slot" data-qr>${book.has_audio ? `<span class="bk-qr" aria-hidden="true"></span><span class="bk-qr-label">🎧 Hier geht's zum Hörbuch</span>` : ''}</div>
    <span class="bk-brand">Mein Heldenkind</span></div>`;
}

// a figure of the book: {url, name} (older JSON: just the url)
const figureOf = (f) => (typeof f === 'string' ? {url: f, name: ''} : (f || {}));

// p3, right of the foreword: "Unsere Helden" - every painted figure with its name, the hero in the middle
function heroesPage(book, {side = 'is-right'} = {}) {
  const figures = (book.figures || []).map(figureOf).filter(f => f.url);
  const order = figures.map((f, i) => ({...f, hero: i === 0}));
  // the hero in the middle, the others around it (as books/pdf.py)
  if (order.length > 2) order.splice(Math.floor(order.length / 2), 0, order.shift());
  return `<div class="bk-page bk-heroespage ${side}">
    <h2>Unsere Helden</h2>
    <span class="bk-ornament">${ORNAMENT}</span>
    ${order.length ? `<div class="bk-figures" style="--n:${order.length}">${order.map(f => `<figure class="${f.hero ? 'is-hero' : ''}"><img src="${esc(f.url)}" alt="">${f.name ? `<figcaption>${esc(f.name)}</figcaption>` : ''}</figure>`).join('')}</div>` : '<div class="bk-figures"></div>'}
    </div>`;
}

// p32, alone on the left: the closing words to the hero, then "Ende"
function closingPage(book, {side = 'is-left', editable = false} = {}) {
  const attrs = editable ? ' role="button" tabindex="0" data-action="edit-closing" aria-label="Schlusswort ändern"' : '';
  return `<div class="bk-page bk-closingpage ${side}${editable ? ' bk-text is-editable' : ''}"${attrs}>
    ${editable ? '<span class="bk-hint">✏️ Schlusswort</span>' : ''}
    <div><span class="bk-heart">${HEART}</span>
      <div class="bk-closing">${paragraphs(book.closing || '')}</div>
      <p class="bk-end">Ende</p>
      <span class="bk-ornament">${ORNAMENT}</span>
      <small>${book.hero ? `Geschrieben und gemalt für ${esc(book.hero)}` : 'Geschrieben und gemalt'}</small></div></div>`;
}

// p2, left of "Unsere Helden": foreword + who it is from, else "Dieses Buch gehört …" (the dedication is on p1)
function forewordPage(book, {side = 'is-left', editable = false} = {}) {
  const attrs = editable ? ' role="button" tabindex="0" data-action="edit-foreword" aria-label="Vorwort schreiben"' : '';
  const words = book.foreword;
  let body;
  if (words) {
    body = `<span class="bk-heart">${HEART}</span><div class="bk-foreword">${paragraphs(words)}</div>
      ${book.foreword && book.foreword_by ? `<p class="bk-foreword-by">– ${esc(book.foreword_by)} –</p>` : ''}`;
  } else {
    body = `<p class="bk-owner-label">Dieses Buch gehört</p><p class="bk-owner">${esc(book.hero || '')}</p>
      ${editable ? '<p class="bk-foreword-add">＋ Vorwort schreiben</p>' : ''}`;
  }
  return `<div class="bk-page bk-forewordpage ${side}${editable ? ' bk-text is-editable' : ''}"${attrs}>
    ${editable ? '<span class="bk-hint">✏️ Vorwort</span>' : ''}<div>${body}</div></div>`;
}

function endpaper(side = 'is-left') {
  return `<div class="bk-page bk-endpaper is-blank ${side}" aria-hidden="true"></div>`;
}

function pictureImg(url) {
  return url ? `<img src="${esc(url)}" alt="" decoding="async">` : '<span class="absolute inset-0 shimmer"></span>';
}

function coverFace(book, url) {
  return `${pictureImg(url)}
    <p class="bk-cover-title">${esc(book.title)}</p>
    ${book.dedication ? `<p class="bk-cover-dedication">${esc(book.dedication)}</p>` : ''}`;
}

function backFace(book) {
  return `<div class="bk-back-face"><div><p>${esc(book.back_text)}</p><p class="bk-back-brand">Mein Heldenkind</p></div></div>`;
}

// Shrink every overflowing text page under `root` until it fits (and remember it).
function fitText(root, font, size, force = false) {
  root.querySelectorAll('[data-fit-text]').forEach(el => {
    if (el.closest('.rd.is-single')) return;     // phones: readable size, the page scrolls instead
    const key = `${font}|${size}|${el.dataset.fitText}`;
    if (!force && fits[key] !== undefined) { el.style.setProperty('--fit', fits[key]); return; }
    const body = el.querySelector('.bk-text-body');
    if (!body || !el.clientHeight || getComputedStyle(el).overflow === 'visible') return;
    const style = getComputedStyle(el);
    const ornament = el.querySelector('.bk-ornament');
    const extra = ornament ? ornament.offsetHeight + parseFloat(getComputedStyle(ornament).marginBottom || 0) : 0;
    const avail = el.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
    let fit = 1;
    el.style.setProperty('--fit', fit);
    while (body.offsetHeight + extra > avail && fit > 0.56) {
      fit = Math.round((fit - 0.04) * 100) / 100;
      el.style.setProperty('--fit', fit);
    }
    fits[key] = fit;
  });
}

module.exports = {esc, ORNAMENT, folio, paragraphs, fitFor, textPage, titlePage, heroesPage, closingPage, figureOf, forewordPage, endpaper, pictureImg, coverFace, backFace, fitText};
